import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { ERROR_CATEGORY_COLORS, ERROR_CATEGORY_LABELS } from '../types';
import type { FavoriteItem } from '../types';

function buildPdfHtml(favorites: FavoriteItem[], title: string, subtitle: string) {
  const grouped: Record<string, FavoriteItem[]> = {};
  favorites.forEach((fav) => {
    const cat = fav.error.category;
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(fav);
  });

  let html = `
    <div style="padding:48px 40px;font-family:'Plus Jakarta Sans','Noto Sans SC','PingFang SC','Microsoft YaHei','Hiragino Sans GB',sans-serif;color:#1e293b;line-height:1.6;background:#fff;">
      <div style="text-align:center;margin-bottom:40px;">
        <div style="font-size:36px;font-weight:800;color:#059669;margin-bottom:8px;">Refine</div>
        <div style="font-size:22px;font-weight:700;color:#1e293b;margin-bottom:6px;">${title}</div>
        <div style="font-size:13px;color:#94a3b8;">${subtitle}</div>
      </div>
      <hr style="border:none;border-top:1px solid #e2e8f0;margin-bottom:36px;">
  `;

  for (const [category, items] of Object.entries(grouped)) {
    const catColor = ERROR_CATEGORY_COLORS[category as keyof typeof ERROR_CATEGORY_COLORS] || '#3b82f6';
    const catLabel = ERROR_CATEGORY_LABELS[category as keyof typeof ERROR_CATEGORY_LABELS] || category;

    html += `
      <div style="margin-bottom:32px;">
        <div style="display:flex;align-items:center;gap:10px;margin-bottom:16px;">
          <span style="display:inline-block;width:14px;height:14px;border-radius:50%;background:${catColor};flex-shrink:0;"></span>
          <span style="font-size:20px;font-weight:700;color:#1e293b;">${catLabel}</span>
          <span style="font-size:12px;font-weight:700;color:#94a3b8;background:#f1f5f9;padding:2px 10px;border-radius:20px;">${items.length}</span>
        </div>
    `;

    for (let i = 0; i < items.length; i += 1) {
      const fav = items[i];
      const zhText = fav.error.explanationZh || '';

      html += `
        <div style="background:#fff;border:1px solid #f1f5f9;border-radius:16px;padding:20px 24px;margin-bottom:12px;page-break-inside:avoid;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;">
            <span style="font-size:12px;font-weight:700;color:#94a3b8;">#${i + 1}</span>
            <span style="font-size:11px;color:#94a3b8;">来自: ${fav.error.essayTitle}</span>
          </div>
          <div style="display:flex;gap:12px;margin-bottom:14px;flex-wrap:wrap;">
            <div style="flex:1;min-width:240px;background:#fef2f2;border:1px solid #fecaca;border-radius:12px;padding:14px 16px;">
              <div style="font-size:10px;font-weight:800;color:#f87171;letter-spacing:1px;margin-bottom:6px;">原句 ✗</div>
              <div style="font-size:13px;color:#991b1b;line-height:1.7;text-decoration:line-through;text-decoration-color:#fca5a5;">${fav.error.originalSentence}</div>
            </div>
            <div style="flex:1;min-width:240px;background:#ecfdf5;border:1px solid #a7f3d0;border-radius:12px;padding:14px 16px;">
              <div style="font-size:10px;font-weight:800;color:#34d399;letter-spacing:1px;margin-bottom:6px;">修改后 ✓</div>
              <div style="font-size:13px;color:#065f46;line-height:1.7;font-weight:600;">${fav.error.correctedSentence}</div>
            </div>
          </div>
      `;

      if (zhText) {
        html += `
          <div style="margin-top:6px;padding-top:6px;border-top:1px solid #f1f5f9;">
            <div style="font-size:10px;font-weight:800;color:#94a3b8;letter-spacing:1px;margin-bottom:4px;">📖 中文解析</div>
            <div style="font-size:12px;color:#64748b;line-height:1.7;">${zhText}</div>
          </div>
        `;
      }

      html += `</div>`;
    }

    html += `</div>`;
  }

  html += `
      <div style="text-align:center;padding-top:24px;border-top:1px solid #e2e8f0;margin-top:24px;">
        <div style="font-size:11px;color:#94a3b8;">Refine 语法错题集 · 考前复习必备</div>
      </div>
    </div>
  `;

  return html;
}

export async function exportFavoritesToPdf(favorites: FavoriteItem[], filename: string, title: string, subtitle: string) {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = '794px';
  container.style.background = '#ffffff';
  container.style.zIndex = '-1';
  container.innerHTML = buildPdfHtml(favorites, title, subtitle);
  document.body.appendChild(container);

  try {
    await document.fonts.ready;
    await new Promise((resolve) => setTimeout(resolve, 200));

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: 794,
      windowWidth: 794,
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const pdfWidth = 210;
    const pdfHeight = 297;
    const ratio = pdfWidth / imgWidth;
    const scaledHeight = imgHeight * ratio;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    if (scaledHeight <= pdfHeight) {
      doc.addImage(imgData, 'JPEG', 0, 0, pdfWidth, scaledHeight);
    } else {
      const pageImgHeight = pdfHeight / ratio;
      let remainingHeight = imgHeight;
      let srcY = 0;
      let pageNum = 0;

      while (remainingHeight > 0) {
        if (pageNum > 0) doc.addPage();

        const sliceHeight = Math.min(pageImgHeight, remainingHeight);
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = imgWidth;
        pageCanvas.height = sliceHeight;
        const ctx = pageCanvas.getContext('2d');
        if (!ctx) throw new Error('无法初始化 PDF 画布');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(canvas, 0, srcY, imgWidth, sliceHeight, 0, 0, imgWidth, sliceHeight);

        const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.95);
        const pageScaledHeight = sliceHeight * ratio;
        doc.addImage(pageImgData, 'JPEG', 0, 0, pdfWidth, pageScaledHeight);

        srcY += sliceHeight;
        remainingHeight -= sliceHeight;
        pageNum += 1;
      }
    }

    doc.save(filename);
  } finally {
    document.body.removeChild(container);
  }
}
